import { LuRotate3D } from 'react-icons/lu';
import { MdLock, MdLockOpen } from 'react-icons/md';
import { PiFootprintsDuotone, PiMapTrifoldDuotone } from 'react-icons/pi';

import { useDollhouse } from '@spazious/dollhouse-engine';
import { Translate } from '@spazious/dollhouse-i18n';
import { useStore } from '@spazious/dollhouse-store';
import { ControlsModeEnum, ToolModeEnum } from '@spazious/dollhouse-types';
import {
  AidTooltip,
  Button,
  ButtonGroup,
  ButtonGroupProps,
  ButtonProps,
  Flex,
  Stack,
  Typography,
} from '@spazious/dollhouse-uikit';

import { useAidTooltipData, useDeviceInfo } from '../../hooks/index.js';
import { ToggleButtonLabel } from '../ToggleButtonLabel/index.js';

export interface CameraControlsButtonProps extends ButtonGroupProps {}

export const CameraControlsButton: React.FC<CameraControlsButtonProps> = (props) => {
  const world = useDollhouse(({ world }) => world);
  const set = useStore(({ set }) => set);
  const controls = useStore(({ controls }) => controls);
  const camera = useStore(({ camera }) => camera);
  const isMobile = useDeviceInfo((state) => state.isMobile);

  const engineControls = useDollhouse(({ controls }) => controls);
  const getAidInfo = useAidTooltipData();
  const buttonSize: ButtonProps['size'] = isMobile ? 'small' : 'medium';

  return (
    <Flex column alignItems="center" gap="5px">
      <ButtonGroup {...props} alignItems="flex-start">
        <AidTooltip {...getAidInfo('orbit-camera-controls')}>
          <ToggleButtonLabel
            actived={controls === ControlsModeEnum.Orbit}
            label={
              <Typography noWrap size="small" variant="label">
                <Translate>Orbit</Translate>
              </Typography>
            }
            size={buttonSize}
            onClick={() => {
              if (controls === ControlsModeEnum.Orbit) {
                // @ts-ignore
                engineControls?.resetViewState();
              } else {
                set(({ tool, ...state }) => ({
                  ...state,
                  controls: ControlsModeEnum.Orbit,
                  camera: {
                    ...state.camera,
                    enablePan: true,
                  },
                  tool: tool === ToolModeEnum.Numbering ? ToolModeEnum.None : tool,
                }));
              }
            }}
          >
            <LuRotate3D />
          </ToggleButtonLabel>
        </AidTooltip>
        <AidTooltip {...getAidInfo('map-camera-controls')}>
          <ToggleButtonLabel
            actived={controls === ControlsModeEnum.Zenithal}
            disabled={!world}
            label={
              <Typography noWrap size="small" variant="label">
                <Translate>2D</Translate>
              </Typography>
            }
            size={buttonSize}
            onClick={() => {
              if (controls === ControlsModeEnum.Zenithal) {
                // @ts-ignore
                engineControls?.resetViewState();
              } else {
                set((state) => ({
                  ...state,
                  controls: ControlsModeEnum.Zenithal,
                  camera: {
                    ...state.camera,
                    enablePan: false,
                  },
                }));
              }
            }}
          >
            <PiMapTrifoldDuotone />
          </ToggleButtonLabel>
        </AidTooltip>
        <AidTooltip {...getAidInfo('look-camera-controls')}>
          <ToggleButtonLabel
            actived={controls === ControlsModeEnum.Walk}
            disabled={!world}
            label={
              <Typography noWrap size="small" variant="label">
                <Translate>Walk</Translate>
              </Typography>
            }
            size={buttonSize}
            onClick={() => {
              if (controls === ControlsModeEnum.Walk) {
                // @ts-ignore
                engineControls?.resetViewState();
              } else {
                set(({ tool, ...state }) => ({
                  ...state,
                  controls: ControlsModeEnum.Walk,
                  camera: {
                    ...state.camera,
                    enablePan: true,
                  },
                  tool: tool === ToolModeEnum.Numbering ? ToolModeEnum.None : tool,
                }));
              }
            }}
          >
            <PiFootprintsDuotone />
          </ToggleButtonLabel>
        </AidTooltip>
      </ButtonGroup>
      <Stack {...props} column padding="3px">
        {controls === ControlsModeEnum.Zenithal && (
          <Button
            size="extraSmall"
            style={{ cursor: 'pointer' }}
            onClick={() => {
              set((state) => ({
                ...state,
                camera: {
                  ...state.camera,
                  enablePan: !camera.enablePan,
                },
              }));
            }}
          >
            {camera.enablePan ? <MdLockOpen /> : <MdLock />}
          </Button>
        )}
      </Stack>
    </Flex>
  );
};
